<script setup lang="ts">
	import type { uiClass } from './index.vue'

	export type ModalOption<T = string> = {
		name: string
		desc?: string
		readonly?: boolean
		key: T
		class?: ClassContext
		descClass?: ClassContext
		icon?: {
			dir: string
			name: string
			pathClass?: string
			wrapperClass?: ClassContext
			svgClass?: ClassContext
		}
		link?: string | Function
		callback?: (e?: string | ModalOption<string>) => void
		disabled?: boolean
		id?: string | number
		label?: string | number
		[key: string]: unknown
	}
	const { cancelBtnPos = 'normal', ...props } = defineProps<{
		title?: string
		paragraph?: string
		show: boolean
		btnTestid?: string
		loading?: boolean
		loadingMore?: boolean
		processing?: boolean
		categoryOptions: ModalOption[]
		bodyClass?: ClassContext
		cancelBtnPos?: 'center' | 'normal'
		caretType?: 'circular' | 'angle-right'
		disableBtn?: boolean
		headWrapperClass?: ClassContext
		ui?: Partial<uiClass>
	}>()
	const activeKey = shallowRef('')

	const selectedOption = ref<ModalOption<string> | null>(null)
	const emit = defineEmits<{
		(event: 'update:show', arg: boolean): boolean
		(event: 'cancel', arg: boolean): boolean
		(event: 'proceed', arg: ModalOption<string>): void
		(event: 'selected', arg: string): void
		(event: 'onTrack-scroll', arg: UIEvent): void
	}>()

	const selectOption = (arg: ModalOption) => {
		if (!arg.readonly) {
			// @ts-ignore
			selectedOption.value = arg
			activeKey.value = arg.key as string
			emit('selected', arg.key)
		}
	}
	const openLink = (arg: ModalOption<string>) => {
		if (selectedOption.value?.key) {
			if (typeof selectedOption.value?.link === 'string') {
				navigateTo(arg)
			} else if (typeof selectedOption.value?.callback === 'function') {
				selectedOption.value?.callback(arg)
			}
			emit('proceed', arg)
			activeKey.value = selectedOption.value.key as string
		}
	}

	const closeModal = () => {
		;(selectedOption.value as unknown) = ref({})
		emit('update:show', false)
		emit('cancel', false)
	}

	watch(
		() => props.show,
		(arg: boolean) => {
			if (arg) {
				activeKey.value = ''
			}
		}
	)
</script>

<template>
	<div>
		<dialogs
			:show="show"
			:btn-position="cancelBtnPos"
			:ui="{
				modal: 'pb-5',
				closeBtn: 'mr-5',
				...ui
			}"
			@change="closeModal"
		>
			<div>
				<div class="mb-4 space-y-3" :class="headWrapperClass">
					<h3 class="mb-0 text-lg font-semibold text-blue-950 md:text-2xl">
						<slot name="title">
							<span v-html="title || ''" />
						</slot>
					</h3>
					<p class="my-0 text-sm font-normal text-gray-600 md:text-base md:leading-[24px]">
						{{ paragraph || '' }}
					</p>
				</div>

				<ul
					id="dialog-body"
					class="mt-12 flex w-full flex-col items-center gap-[32px]"
					:class="bodyClass"
					@scroll="(event: Event) => emit('onTrack-scroll', event as UIEvent)"
				>
					<li
						v-if="Array.isArray(categoryOptions) && categoryOptions.length === 0 && loading"
						class="w-full space-y-5"
					>
						<loader-skeleton :height="100" :count="3" :max-width="false" class="w-full" />
					</li>
					<li v-else-if="Array.isArray(categoryOptions) && categoryOptions.length === 0" class="py-5">
						<slot name="empty" />
					</li>
					<template v-else>
						<template v-for="(option, index) in categoryOptions" :key="index">
							<li
								v-if="!option?.disabled"
								class="flex w-full items-center justify-between rounded-[8px] border px-3 py-4 transition ease-linear sm:px-6 sm:py-5"
								:class="[
									option.key === activeKey ? 'border-primary-2' : 'border-purple-100',
									option?.class,
									{
										'cursor-pointer bg-purple-25': !option?.readonly,
										'cursor-not-allowed bg-gray-25 text-gray-300': option?.readonly
									}
								]"
								:data-testid="option.key"
								:disabled="option?.readonly || false"
								@click="selectOption(option)"
							>
								<div class="flex-wra flex items-center gap-3 sm:gap-[20px]">
									<div
										v-if="option.icon"
										class="flex h-[40px] w-[40px] items-center justify-center rounded-full border transition-all sm:h-[48px] sm:w-[48px]"
										:class="option.icon?.wrapperClass"
									>
										<lazy-tools-icon
											:dir="option.icon?.dir"
											:icon="option.icon?.name"
											:path-class="option.icon?.pathClass"
											:svg-class="`${option.icon?.svgClass ?? ''} sm:h-[25px] md:w-[25px] w-[20px] h-[20px]`"
										/>
									</div>

									<div class="space-y-1">
										<h5
											class="md:text-md my-0 items-center text-base font-medium capitalize text-gray-700 sm:gap-x-2 md:flex"
										>
											<p>{{ option.name }}</p>
											<slot name="sup" :option="option" />
										</h5>
										<p
											:class="[option.descClass || 'text-gray-700']"
											class="my-0 text-xs font-normal md:text-xs"
										>
											{{ option.desc }}
										</p>
									</div>
								</div>

								<div
									v-if="!caretType || caretType === 'circular'"
									class="relative ml-5 h-[18px] min-w-[18px] flex-none rounded-full border transition-all ease-in before:absolute before:bottom-0 before:left-0 before:right-0 before:top-0 before:m-auto before:h-[5px] before:w-[5px] before:rounded-full before:bg-white"
									:class="[
										option.key === activeKey ? 'border-primary-2 bg-primary-2' : 'border-gray-400'
									]"
								></div>
								<lazy-tools-icon
									v-else-if="caretType === 'angle-right'"
									class="block"
									icon="caret-right"
									:height="24"
									:width="24"
									dir="arrows"
								/>
							</li>
						</template>
						<lazy-tools-icon
							v-show="loadingMore"
							icon="eclipse"
							dir="line"
							circle-class="stroke-gray-300"
							:height="30"
							:width="30"
						/>
					</template>
				</ul>
				<slot name="btn">
					<a-button
						v-if="categoryOptions.length && !disableBtn"
						class="default-btn mt-10 w-full justify-center bg-primary-2"
						:disabled="!selectedOption?.key"
						:data-testid="btnTestid"
						:loading="loading || processing"
						@click="() => openLink(selectedOption!)"
					>
						Continue
					</a-button>
				</slot>
			</div>
		</dialogs>
	</div>
</template>
